import { z } from 'zod';
import { geocodeAccuracySchema } from './client.js';
import { disciplineSchema } from './payer.js';
import { restricted } from './redaction.js';
/**
 * The caregiver record (§6.3.5, slice-02 §7).
 *
 * ## One restricted field, and it is not PHI
 *
 * `basePayRateCents` sits behind `caregiver:read_pay`, which §10.4 **denies to
 * SCHEDULER deliberately**: schedulers see the whole roster, and pay rates are
 * not theirs to see. That makes this the first field in the product restricted
 * for a reason that is not patient confidentiality — it is an employment fact,
 * and the person who assigns shifts is not the person who sets wages.
 *
 * It is declared once, on the summary, and the detail extends the summary — so
 * the **list** carries the same restriction as the record, from the same
 * declaration. A list is where a field is most often forgotten, and slice-02
 * exit criterion 8 names the list first for that reason.
 *
 * Carried on the summary at all, rather than left off, for the argument
 * `clientSummarySchema` already makes: omitting it would hide the bug this file
 * exists to prevent, because a list that never carried the field proves nothing
 * about a list that later does.
 */
export const employmentTypeSchema = z.enum([
    'W2_FULL_TIME',
    'W2_PART_TIME',
    'W2_PRN',
    'CONTRACTOR_1099',
]);
export const caregiverStatusSchema = z.enum([
    'APPLICANT',
    'ONBOARDING',
    'ACTIVE',
    'INACTIVE',
    'TERMINATED',
    'DO_NOT_RETURN',
]);
/*
 * `geocodeAccuracySchema` is not redefined here.
 *
 * `client.ts` already has it, because a client address is geocoded by the same
 * service against the same enum. A second copy would be two schemas for one
 * database type, drifting the first time a tier is added — and the tiers are
 * what decide a geofence radius, so a drift would be a caregiver matched
 * against a radius the EVV slice does not recognise.
 */
/** The one field `caregiver:read_pay` governs. Declared once; see the header. */
const pay = (schema) => restricted(schema, 'caregiver:read_pay');
/**
 * The roster row.
 *
 * `basePayRateCents` is a **number** rather than a bigint over the wire.
 * `bigint` is the storage type because rule 7 forbids floats in money, and JSON
 * has no bigint at all — but cents stay exact in a JS number until roughly
 * ninety trillion dollars, which is a limit this product will not meet.
 */
export const caregiverSummarySchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    employeeNumber: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    status: caregiverStatusSchema,
    employmentType: employmentTypeSchema,
    disciplines: z.array(disciplineSchema),
    languages: z.array(z.string()),
    hireDate: z.string().nullable(),
    maxWeeklyHours: z.number().int().nullable(),
    basePayRateCents: pay(z.number().int().nullable()),
});
/** The matching attributes §6.3.5 lists, grouped because they are read together. */
export const caregiverMatchingSchema = z.object({
    hasVehicle: z.boolean(),
    willTransportClient: z.boolean(),
    smokingOk: z.boolean(),
    petsOk: z.boolean(),
    maxTravelMinutes: z.number().int().nullable(),
});
export const caregiverDetailSchema = caregiverSummarySchema.extend({
    phone: z.string().nullable(),
    terminationDate: z.string().nullable(),
    terminationReason: z.string().nullable(),
    npi: z.string().nullable(),
    stateRegistryId: z.string().nullable(),
    homeLine1: z.string().nullable(),
    homeLine2: z.string().nullable(),
    homeCity: z.string().nullable(),
    homeState: z.string().nullable(),
    homePostalCode: z.string().nullable(),
    /**
     * Visible rather than absent when it fails, exactly as `client_address` is
     * (§11.3, slice-01 exit criterion 5). A failed geocode that disappeared would
     * leave a caregiver silently unmatched on travel distance.
     */
    homeGeocodeAccuracy: geocodeAccuracySchema.nullable(),
    homeGeocodedAt: z.string().nullable(),
    homeTimezone: z.string().nullable(),
    matching: caregiverMatchingSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const caregiverListSchema = z.object({
    caregivers: z.array(caregiverSummarySchema),
    total: z.number().int(),
});
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-03-11 — four digits, two, two.');
/**
 * Creating a caregiver creates the login too.
 *
 * `user_id` is not accepted from the caller. A caregiver row points at a user
 * with `audience = CAREGIVER` and the database enforces that with a
 * three-column foreign key; letting a client supply the id would make the
 * common failure "you passed a staff user" rather than making it unreachable.
 * The service mints the user, with the caregiver audience, in the same
 * transaction.
 */
export const createCaregiverSchema = z.object({
    firstName: z.string().trim().min(1).max(120),
    lastName: z.string().trim().min(1).max(120),
    email: z.string().trim().email().max(200),
    phone: z.string().trim().max(32).nullish(),
    employeeNumber: z.string().trim().max(64).nullish(),
    employmentType: employmentTypeSchema,
    hireDate: isoDate.nullish(),
    disciplines: z.array(disciplineSchema).min(1).max(7),
    languages: z.array(z.string().trim().min(1).max(32)).max(20).default([]),
    homeLine1: z.string().trim().max(200).nullish(),
    homeLine2: z.string().trim().max(200).nullish(),
    homeCity: z.string().trim().max(120).nullish(),
    homeState: z.string().trim().length(2).nullish(),
    homePostalCode: z.string().trim().max(12).nullish(),
    maxWeeklyHours: z.number().int().positive().max(168).nullish(),
    maxTravelMinutes: z.number().int().positive().max(600).nullish(),
    hasVehicle: z.boolean().default(false),
    willTransportClient: z.boolean().default(false),
    smokingOk: z.boolean().default(true),
    petsOk: z.boolean().default(true),
    npi: z
        .string()
        .regex(/^\d{10}$/, 'An NPI is exactly ten digits.')
        .nullish(),
    stateRegistryId: z.string().trim().max(64).nullish(),
    /** Cents. Rejected outright without `caregiver:write_pay`… see the note below. */
    basePayRateCents: z.number().int().min(0).nullish(),
});
/**
 * Everything except status, which moves only through its own routes.
 *
 * The same rule `updateClientSchema` follows and for the same reason: a PATCH
 * that could set `status` would be a status change with no reason, no actor and
 * no gate — and the ACTIVE gate is the one thing in this slice that refuses
 * work.
 */
export const updateCaregiverSchema = createCaregiverSchema.partial().strict();
/**
 * A status move that is not the end of employment.
 *
 * TERMINATED and DO_NOT_RETURN are **not** in this enum. They end somebody's
 * job, they hold `caregiver:terminate` rather than `caregiver:update` in §10.4,
 * and they go through `POST /v1/caregivers/:id/terminate`.
 *
 * Split at the route rather than branching inside the handler, exactly as
 * client discharge is: §8 assumes the permission is known before the handler
 * runs, and a guard that had to read the body to decide would break that.
 */
export const changeCaregiverStatusSchema = z.object({
    toStatus: z.enum(['APPLICANT', 'ONBOARDING', 'ACTIVE', 'INACTIVE']),
    reason: z.string().trim().min(1).max(2_000),
});
export const terminateCaregiverSchema = z.object({
    reason: z.string().trim().min(1).max(2_000),
    /** Defaults to today. Backdating is ordinary — HR hears late. */
    effectiveAt: isoDate.optional(),
    /**
     * Whether this person may be re-hired.
     *
     * A separate flag rather than a separate call, because it is a decision made
     * at the same moment by the same person, and asking twice is how it gets
     * forgotten on the one record where it mattered.
     */
    doNotReturn: z.boolean().default(false),
});
export const caregiverListQuerySchema = z.object({
    /**
     * Matches a name, an employee number or an email.
     *
     * Deliberately **not** the pay rate. Searching "45000" must not reveal who
     * earns it to somebody refused the column — a filter is a read, and a read
     * that answers by presence is a read. `caregiver-redaction.spec.ts` asserts
     * it.
     */
    search: z.string().trim().max(120).optional(),
    status: caregiverStatusSchema.optional(),
    discipline: disciplineSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// ---------------------------------------------------------------------------
// Onboarding (§17.3)
// ---------------------------------------------------------------------------
export const onboardingItemSchema = z.object({
    id: z.string().uuid(),
    key: z.string(),
    label: z.string(),
    blocksScheduling: z.boolean(),
    sortOrder: z.number().int(),
    completed: z.boolean(),
    completedAt: z.string().nullable(),
    completedBy: z.string().uuid().nullable(),
});
export const caregiverOnboardingSchema = z.object({
    items: z.array(onboardingItemSchema),
    /**
     * Echoed rather than left to the caller to compute, because the console and
     * the ACTIVE gate must agree about it and two implementations would drift.
     */
    blocksActivation: z.boolean(),
});
export const completeOnboardingItemSchema = z.object({
    note: z.string().trim().max(2_000).nullish(),
});
/** For tests and for the audit hasher, derived rather than restated. */
export const CAREGIVER_RESTRICTED_FIELDS = ['basePayRateCents'];
//# sourceMappingURL=caregiver.js.map